import { css, styled } from 'styled-components';

import { Shade60Color, Shade00Color } from 'common/dist/styles/common';

import { CARD_DIMENSIONS } from '../../scenes/Form/UploadCardContainer/types';

export const PermissionsNotFoundWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  border: 1px dashed ${Shade60Color};
  border-radius: 4px;
  padding: 24px;
  max-width: 640px;
  aspect-ratio: 640 / 480;
  box-sizing: border-box;

  @media only screen and (min-width: 769px) {
    padding: 24px 40px;
  }
`;

export const Wrapper = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 32px;
  width: 100%;
  height: 100%;
`;

export const Container = styled.div.attrs<{
  width: number;
  height: number;
  maxWidth?: number;
  maxHeight?: number;
}>((props) => ({
  width: props.width,
  height: props.height,
  maxWidth: props.maxWidth,
  maxHeight: props.maxHeight,
}))`
  position: relative;
  overflow: hidden;
  width: ${(props) => (props.width ? `${props.width}px` : '100%')};
  height: ${(props) => props.height && `${props.height}px`};
  max-width: ${(props) => props.maxWidth && `${props.maxWidth}px`};
  max-height: ${(props) => props.maxHeight && `${props.maxHeight}px`};
`;

export const Canvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
`;

export const Video = styled.video.attrs<{ flipped: boolean }>((props) => ({
  flipped: props.flipped,
}))`
  ${(props) => {
    if (props.flipped) {
      return css`
        transform: scale(-1, 1);
      `;
    }
  }};
  position: absolute;
  width: 100%;
`;

const OVERLAY_RATIO = CARD_DIMENSIONS.height / CARD_DIMENSIONS.width;
export const MIN_OVERLAY_OFFSET_PX = 12;

//
// PROBLEM:
// How do we make sure that the overlay is responsive but still maintains the same aspect ratio?
//
//
// We need to determine the correct vertical offset given the horizontal offset in order to maintain the same aspect ratio.
//
// Let's create a visual:
//  .--------------------------------------┐
// │                   ⋀                   │
// │                   │ g                 │
// │                   ⋁                   │
// │       .-----------------------.       │
// │       │                       │       │
// │   w   │                       │       │
// │<----->│                       │ b     │ y
// │       │                       │       │
// │       │                       │       │
// │       '-----------------------'       │
// │                   a                   │
// │                                       │
// │                                       │
//  '--------------------------------------'
//                     x
//
//
// The larger bounding box is the canvas container with dimensions x * y.
// The smaller box represents the overlay within the container.
//
//
// EQUATIONS:
//   x = a + 2w
//   y = b + 2g
//
//   And we also know the aspect ratio b/a
//
//    b         2.125
//   --- = c = --------
//    a          3.37
//
// If we rearrange the above equations, we get
// x - 2w = a
// y - 2g = b
//
// We can reorder the equations and get
// y - 2g = b
// x - 2w = a
//
// If we divide the first equation by the second:
//  y - 2g     b
// -------- = --- = c
//  x - 2w     a
//
// We know x and y. We know c. So we just need to figure out the offsets.
// If we pick a value for one of the offsets, we can calculate what the other offset should be.
//
// Let's solve for the vertical offset g.
//  y - 2g     b
// -------- = --- = c
//  x - 2w     a
//
// We can multiply by the denominator
// y - 2g = c(x - 2w)
//
// Isolate g
// y - c(x - 2w) = 2g
//
// And divide by 2
//  y - c(x - 2w)
// --------------- = g
//        2
//
// If we wanted to calculate the horizontal offset, we would end up with an equation like:
//
//  x     y - 2g
// --- - -------- = w
//  2       2c
//
// For the most part, we can calculate the offset based on the horizontal offset,
// but if the video is too wide, then the overlay will end up having a height larger than
// the video.
// More specifically, if b > y, the overlay will extend beyond the dimensions of
// the video. In this case, we should calculate the horizontal offset based on
// the vertical offset.
export const getOverlayOffset = (
  containerHeight: number,
  containerWidth: number,
): { horizontal_offset: number; vertical_offset: number } => {
  const overlay_height =
    OVERLAY_RATIO * (containerWidth - 2 * MIN_OVERLAY_OFFSET_PX);
  if (overlay_height < containerHeight) {
    return {
      horizontal_offset: MIN_OVERLAY_OFFSET_PX,
      vertical_offset: (containerHeight - overlay_height) / 2,
    };
  } else {
    const horizontal_offset =
      (containerWidth -
        (containerHeight - 2 * MIN_OVERLAY_OFFSET_PX) / OVERLAY_RATIO) /
      2;
    return { horizontal_offset, vertical_offset: MIN_OVERLAY_OFFSET_PX };
  }
};

export const Overlay = styled.div.attrs<{
  loading: boolean;
  containerHeight: number;
  containerWidth: number;
}>((props) => ({
  containerHeight: props.containerHeight,
  containerWidth: props.containerWidth,
  loading: props.loading,
}))`
  position: absolute;

  ${(props) => {
    const { horizontal_offset, vertical_offset } = getOverlayOffset(
      props.containerHeight,
      props.containerWidth,
    );

    return css`
      top: ${vertical_offset}px;
      bottom: ${vertical_offset}px;

      left: ${horizontal_offset}px;
      right: ${horizontal_offset}px;
    `;
  }}

  box-shadow: 0 0 20px 56px rgba(0, 0, 0, 0.6);
  border: 2px solid ${Shade00Color};
  border-radius: 10px;

  background: ${(props) => (props.loading ? `${Shade60Color}` : 'initial')};
  opacity: ${(props) => (props.loading ? '60%' : 'initial')};
`;

export const LoaderWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const InstructionsAndActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  max-width: var(--content-max-width);
  width: 100%;
`;
